import { TimerMode } from '@/components/settings/timer-configuration/types'
import { PhaseType } from '@/redux/slices/timerConfigurationSlice'
import {
  NegotiationMember,
  NegotiationType,
} from '@/redux/types/negotiationWebsocket'

export enum Status {
  Pending = 'pending',
  Accepted = 'accepted',
  Rejected = 'rejected',
  Invite = 'Invite',
  Terminated = 'terminated',
  Owner = 'owner',
  Draft = 'draft',
}

export enum RoleChoice {
  PLAYER = 'player',
  OWNER = 'owner',
  CO_PROFESSOR = 'co-professor',
}

export enum GameRoomStatus {
  NOT_STARTED = 1,
  IN_PROGRESS = 2,
  COMPLETED = 3,
  PAUSED = 4,
}

/** True when the game has started and not completed (includes paused; see gameroom pause/resume). */
export function isGameRoomSessionActive(
  status: GameRoomStatus | undefined
): boolean {
  return (
    status === GameRoomStatus.IN_PROGRESS ||
    status === GameRoomStatus.PAUSED
  )
}

export enum CardType {
  ROOM = 'room',
  ADVISOR_CHAT = 'advisor_chat',
  NEGOTIATION = 'negotiation_chat',
}

export enum ResourceEnforcementMode {
  BLOCK = 'block',
  WARN = 'warn',
}

export interface RoomMember {
  gamealias: string
  member_username: string | null
  member_email: string
  member_name: string
  invitation_status: Status
  member_profile_picture?: string
  member_id?: string
  role: RoleChoice
  teamId?: number | null
}

export interface EventMediaItem {
  id: number
  type: string
  url: string
  caption?: string
}

export interface GameroomEvent {
  id: number
  title: string
  text: string
  event_datetime: string
  is_released: boolean
  released_at?: string
  released_round_number?: number | null
  released_phase_type?: PhaseType | null
  created_at?: string
  media?: EventMediaItem[]
  use_ttq?: boolean
  /** False means the event is addressed to the players and teams below. */
  affects_all_players?: boolean
  /** RoomMember ids. Read from the server; write via assigned_player_ids. */
  assigned_players?: number[]
  affected_teams?: number[]
  assigned_player_ids?: number[]
  affected_team_ids?: number[]
  /** Set on first release and never cleared — the audience is locked once true. */
  has_ever_been_released?: boolean
}

export interface Negotiation {
  id: string
  inviter: string
  members: NegotiationMember[]
  starttime: string
  round_number?: number
  member_count: number
  is_one_on_one: boolean
  negotiation_type: NegotiationType
  end_time?: string
  phase_id?: number
  room_id?: string
}

export interface Room {
  roomid: string
  roomname: string | null
  scenario_title: string | null
  game_mode: TimerMode
  total_rounds: number
  creationtime: string
  invite_code: string
  owner: string | null
  is_ready_to_start: boolean
  is_ready_for_simulation?: boolean
  status: GameRoomStatus
  events: GameroomEvent[]
  negotiations: Negotiation[]
  start_time: string
  ends_in: number
  pending_adjudications_count?: number
  advisors_count?: number
  players_count?: number
  invitation_status: Status
  /** Requesting user's role in this room (dashboard list). Used to route
   *  owners/co-professors to the room instead of the join-room flow. */
  role?: RoleChoice | null
  gameroom_llm: LLMChoice
  auto_approve: boolean
  enable_chat_history_summarization: boolean
  is_locked: boolean
  is_simulating?: boolean
  charter_enabled?: boolean
  charter_gate_enabled?: boolean
  /** True when this room is a standalone Charter Class rather than a game:
   *  no rounds/phases, and the backend refuses every game action on it. */
  is_charter_only?: boolean
  resource_limits_enabled?: boolean
  resource_enforcement_mode?: ResourceEnforcementMode
  default_currency_amount?: number | null
  default_message_limit?: number | null
  default_token_limit?: number | null
  openai_api_key_configured?: boolean
  claude_api_key_configured?: boolean
  current_session_uuid?: string | null
  team_orientation_shown?: boolean
}

export interface DashboardKPIs {
  total_games: number
  scheduled_games: number
  active_games: number
  completed_games: number
  pending_adjudications: number
  distinct_players: number
  distinct_professors: number
  /** Charter Classes are counted separately — the counters above are games. */
  charter_classes: number
  charter_class_students: number
}

export interface RoomState {
  currentRoom: Room | null
  rooms: Room[]
  /** Charter Classes, kept separate from `rooms` so the game dashboard and the
   *  Charter Classes dashboard don't overwrite each other's list. */
  charterClasses: Room[]
  isLoading: boolean
  error: string | null
  inviteStatus: {
    isLoading: boolean
    error: string | null
    success: boolean
  }

  roomMembers: RoomMember[]
  dashboardKPIs: DashboardKPIs
}

export enum LLMChoice {
  OPENAI = 'openai',
  CLAUDE = 'claude',
}

export enum RoomModalMode {
  CREATE = 'create',
  EDIT = 'edit',
}
export enum ModeType {
  ROOM = 'room',
  TEAM = 'team',
}

export interface GameSummaryMetadata {
  players: Array<{
    id: number
    member__id: number
    member__email: string
    member__first_name: string
    member__last_name: string
    gamealias: string | null
    team__id: number | null
    team__name: string | null
  }>
  teams: Array<{
    id: number
    name: string
  }>
  phases: Array<{
    id: number
    phase_type: string
    game_round__round_number: number
  }>
  rounds: Array<{
    round_number: number
    status: string
  }>
}

export interface GameSummaryResponse {
  metadata: GameSummaryMetadata
  summary_available: boolean
  summary_ready: boolean
}
